import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeftIcon,
  RefreshCwIcon,
  XCircleIcon,
  TrophyIcon,
  ChevronDownIcon,
} from "lucide-react";
import { usePermissions } from "../../../PermissionsProvider";
import {
  CompassService,
  CompassSearch,
  CompassSearchRun,
} from "../../../services/CompassService";
import { Card, CardContent } from "../../ui/card";
import { Badge } from "../../ui/badge";
import { Button } from "../../ui/button";
import OverflowTabs, { TabItem } from "../../ui/overflow-tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../ui/alert-dialog";



const STATUS_VARIANT: Record<string, string> = {
  PENDING: "bg-yellow-500/15 text-yellow-600 dark:text-yellow-400 border-yellow-500/20",
  RUNNING: "bg-blue-500/15 text-blue-600 dark:text-blue-400 border-blue-500/20",
  COMPLETED: "bg-green-500/15 text-green-600 dark:text-green-400 border-green-500/20",
  FAILED: "bg-red-500/15 text-red-600 dark:text-red-400 border-red-500/20",
  CANCELLED: "bg-muted text-muted-foreground border-border",
};

const KEY_HYPERPARAMS = [
  "learning_rate",
  "rank",
  "alpha",
  "num_train_epochs",
  "dropout",
  "weight_decay",
];

function formatDate(ts: number | string | null): string {
  if (!ts) return "\u2014";
  const d = new Date(ts);
  return d.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function formatValue(v: unknown): string {
  if (typeof v === "number" && !Number.isInteger(v)) return v.toPrecision(4);
  if (typeof v === "boolean") return v ? "Yes" : "No";
  return String(v);
}

const CompassSearchDetail: React.FC = () => {
  const { searchId } = useParams<{ searchId: string }>();
  const [searchParams] = useSearchParams();
  const companyId = searchParams.get("company_id") || undefined;
  const navigate = useNavigate();
  const { apiKey } = usePermissions();
  const [isLoading, setIsLoading] = useState(true);
  const [search, setSearch] = useState<CompassSearch | null>(null);
  const [runs, setRuns] = useState<CompassSearchRun[]>([]);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [activeTab, setActiveTab] = useState("overview");
  const [expandedRuns, setExpandedRuns] = useState<Set<number>>(new Set());

  const loadSearch = useCallback(() => {
    if (!apiKey || !searchId) return;
    CompassService.getSearchById(parseInt(searchId), setIsLoading, apiKey, companyId).subscribe(
      (data: CompassSearch) => {
        setSearch(data);
        setIsLoading(false);
      },
      (error: unknown) => {
        console.error("Failed to load search:", error);
        toast.error("Failed to load search");
        setIsLoading(false);
      }
    );
  }, [apiKey, searchId, companyId]);

  const loadRuns = useCallback(() => {
    if (!apiKey || !searchId) return;
    CompassService.getSearchRuns(parseInt(searchId), null, apiKey, companyId).subscribe(
      (data: { items: CompassSearchRun[] }) => setRuns(data.items || []),
      (error: unknown) => console.error("Failed to load runs:", error)
    );
  }, [apiKey, searchId, companyId]);

  useEffect(() => {
    loadSearch();
    loadRuns();
  }, [loadSearch, loadRuns]);

  const handleCancel = () => {
    if (!apiKey || !searchId) return;
    CompassService.cancelSearch(parseInt(searchId), setCancelling, apiKey, companyId).subscribe(
      () => {
        toast.success("Search cancelled");
        setCancelDialogOpen(false);
        setCancelling(false);
        loadSearch();
        loadRuns();
      },
      (error: unknown) => {
        console.error("Failed to cancel search:", error);
        toast.error("Failed to cancel search");
        setCancelling(false);
      }
    );
  };

  const toggleRunExpanded = (runId: number) => {
    setExpandedRuns((prev) => {
      const next = new Set(prev);
      if (next.has(runId)) next.delete(runId);
      else next.add(runId);
      return next;
    });
  };

  const waves = Array.from(new Set(runs.map((r) => r.wave))).sort((a, b) => a - b);
  const bestRun = runs
    .filter((r) => r.accuracy != null)
    .sort((a, b) => (b.accuracy ?? 0) - (a.accuracy ?? 0))[0];

  if (isLoading && !search) {
    return <div className="p-6 text-center text-muted-foreground">Loading...</div>;
  }
  if (!search) {
    return <div className="p-6 text-center text-muted-foreground">Search not found.</div>;
  }

  const isActive = search.status === "RUNNING" || search.status === "PENDING";

  const overviewContent = (
    <div className="space-y-8">
      {/* Combined Details, Stats & Configuration */}
      <Card className="border-border">
        <CardContent className="p-6 space-y-8">
          {/* Top stats row */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            <div>
              <p className="text-sm text-muted-foreground font-gc-reg mb-1">Base Model</p>
              <p className="text-lg font-gc-med text-foreground">{search.base_model}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground font-gc-reg mb-1">Method</p>
              <p className="text-lg font-gc-med text-foreground">{search.training_method}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground font-gc-reg mb-1">Best Accuracy</p>
              <p className="text-lg font-gc-med text-foreground">
                {search.best_accuracy != null
                  ? `${(search.best_accuracy * 100).toFixed(1)}%`
                  : "\u2014"}
              </p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground font-gc-reg mb-1">Threshold</p>
              <p className="text-lg font-gc-med text-foreground">
                {(search.accuracy_threshold * 100).toFixed(0)}%
              </p>
            </div>
          </div>

          <hr className="border-border" />

          {/* Details */}
          <div>
            <h3 className="text-lg font-gc-med text-foreground mb-4">Details</h3>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
              <div>
                <p className="text-sm text-muted-foreground font-gc-reg mb-1">Source Application</p>
                <div>
                  {search.source_application_id ? (
                    <Link
                      to={`/application/${search.source_application_id}`}
                      className="text-maitai-green hover:underline text-sm"
                    >
                      {search.source_app_name || `Application #${search.source_application_id}`}
                    </Link>
                  ) : (
                    <span className="text-base font-gc-reg text-foreground">{"\u2014"}</span>
                  )}
                </div>
              </div>

              {search.meta?.intent_id && (
                <div>
                  <p className="text-sm text-muted-foreground font-gc-reg mb-1">Intent</p>
                  <div>
                    <Link
                      to={`/application/${search.source_application_id}/intent/${search.meta.intent_id}`}
                      className="text-maitai-green hover:underline text-sm"
                    >
                      {search.intent_name || `Intent #${search.meta.intent_id}`}
                    </Link>
                  </div>
                </div>
              )}

              <div>
                <p className="text-sm text-muted-foreground font-gc-reg mb-1">Composition</p>
                <div>
                  {search.composition_id ? (
                    <Link
                      to={`/forge/compositions/${search.composition_id}`}
                      className="text-maitai-green hover:underline text-sm"
                    >
                      {search.composition_name || `Composition #${search.composition_id}`}
                    </Link>
                  ) : (
                    <span className="text-base font-gc-reg text-foreground">{"\u2014"}</span>
                  )}
                </div>
              </div>

              <div>
                <p className="text-sm text-muted-foreground font-gc-reg mb-1">Compass Application</p>
                <div>
                  {search.application_id ? (
                    <Link
                      to={`/application/${search.application_id}`}
                      className="text-maitai-green hover:underline text-sm"
                    >
                      {search.compass_app_name || `Application #${search.application_id}`}
                    </Link>
                  ) : (
                    <span className="text-base font-gc-reg text-foreground">{"\u2014"}</span>
                  )}
                </div>
              </div>

              <div>
                <p className="text-sm text-muted-foreground font-gc-reg mb-1">Date Created</p>
                <p className="text-base font-gc-reg text-foreground">
                  {formatDate(search.date_created)}
                </p>
              </div>
              {search.date_completed && (
                <div>
                  <p className="text-sm text-muted-foreground font-gc-reg mb-1">Date Completed</p>
                  <p className="text-base font-gc-reg text-foreground">
                    {formatDate(search.date_completed)}
                  </p>
                </div>
              )}
            </div>
          </div>

          <hr className="border-border" />

          {/* Configuration */}
          <div>
            <h3 className="text-lg font-gc-med text-foreground mb-4">Configuration</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              <div>
                <p className="text-sm text-muted-foreground font-gc-reg mb-1">Max Waves</p>
                <p className="text-base font-gc-reg text-foreground">{search.max_waves}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground font-gc-reg mb-1">Runs per Wave</p>
                <p className="text-base font-gc-reg text-foreground">{search.runs_per_wave}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground font-gc-reg mb-1">Total Runs</p>
                <p className="text-base font-gc-reg text-foreground">{runs.length}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground font-gc-reg mb-1">Best Run ID</p>
                <p className="text-base font-gc-reg text-foreground">{search.best_run_id ?? "\u2014"}</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Best Configuration — shown below only when available */}
      {bestRun && (
        <Card className="border-green-500/30 bg-green-500/5">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <TrophyIcon className="w-5 h-5 text-green-600" />
                <h3 className="text-lg font-gc-med">Best Configuration</h3>
                <Badge className="bg-green-500/15 text-green-600 border-green-500/20">
                  {((bestRun.accuracy ?? 0) * 100).toFixed(1)}% accuracy
                </Badge>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  const params = new URLSearchParams();
                  for (const [k, v] of Object.entries(bestRun.hyperparameters)) {
                    params.set(k, String(v));
                  }
                  if (search.base_model) params.set("base_model", search.base_model);
                  if (search.training_method) params.set("training_method", search.training_method);
                  navigate(`/forge/finetuning/new?${params.toString()}`);
                }}
              >
                Use These Settings
              </Button>
            </div>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Parameter</TableHead>
                  <TableHead>Value</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {Object.entries(bestRun.hyperparameters).map(([key, val]) => (
                  <TableRow key={key}>
                    <TableCell className="font-mono text-xs text-muted-foreground">{key}</TableCell>
                    <TableCell className="font-mono text-xs font-medium">{formatValue(val)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </div>
  );

  const waveRunsContent = (
    <div className="space-y-6">
      {waves.length === 0 && !isLoading && (
        <Card>
          <CardContent className="py-8 text-center text-muted-foreground">
            No runs yet. Waiting for Wave 1 to launch.
          </CardContent>
        </Card>
      )}

      {waves.map((wave) => {
        const waveRuns = runs
          .filter((r) => r.wave === wave)
          .sort((a, b) => (b.accuracy ?? -1) - (a.accuracy ?? -1));

        return (
          <Card key={wave}>
            <CardContent className="p-0">
              <div className="px-4 py-3 border-b flex items-center justify-between">
                <h3 className="font-semibold text-sm">Wave {wave}</h3>
                <span className="text-xs text-muted-foreground">
                  {waveRuns.filter((r) => r.status === "COMPLETED").length}/{waveRuns.length} completed
                </span>
              </div>
              <Table className="table-fixed w-full">
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[3%]" />
                    <TableHead className="w-[18%]">Run Name</TableHead>
                    <TableHead className="w-[10%]">Status</TableHead>
                    <TableHead className="w-[10%]">Accuracy</TableHead>
                    <TableHead className="w-[10%]">Overfitting</TableHead>
                    <TableHead className="w-[10%]">Underfitting</TableHead>
                    <TableHead className="w-[39%]">Key Hyperparameters</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {waveRuns.map((run) => {
                    const isExpanded = expandedRuns.has(run.id);
                    const isBest = bestRun && run.id === bestRun.id;
                    const keyParams = KEY_HYPERPARAMS.filter(
                      (k) => run.hyperparameters[k] !== undefined
                    );
                    const allParams = Object.entries(run.hyperparameters).filter(
                      ([k]) => !KEY_HYPERPARAMS.includes(k)
                    );

                    return (
                      <React.Fragment key={run.id}>
                        <TableRow
                          className={`cursor-pointer hover:bg-muted/50 ${isBest ? "bg-green-500/5" : ""}`}
                          onClick={() => toggleRunExpanded(run.id)}
                        >
                          <TableCell className="w-8 pr-0">
                            <ChevronDownIcon
                              className={`w-4 h-4 text-muted-foreground transition-transform ${
                                isExpanded ? "rotate-0" : "-rotate-90"
                              }`}
                            />
                          </TableCell>
                          <TableCell className="font-medium text-sm">
                            <span className="inline-flex items-center gap-1.5">
                              {run.run_name || `Run #${run.finetune_run_id}`}
                              {isBest ? (
                                <TrophyIcon className="w-3.5 h-3.5 text-green-600 shrink-0" />
                              ) : (
                                <span className="w-3.5 h-3.5 shrink-0 inline-block" />
                              )}
                            </span>
                          </TableCell>
                          <TableCell>
                            <Badge className={STATUS_VARIANT[run.status] || ""}>
                              {run.status}
                            </Badge>
                          </TableCell>
                          <TableCell className="font-medium">
                            {run.accuracy != null
                              ? `${(run.accuracy * 100).toFixed(1)}%`
                              : "\u2014"}
                          </TableCell>
                          <TableCell>
                            {run.loss_curve_analysis ? (
                              <span
                                className={
                                  run.loss_curve_analysis.overfitting_score > 0.5
                                    ? "text-red-500"
                                    : "text-green-500"
                                }
                              >
                                {run.loss_curve_analysis.overfitting_score.toFixed(2)}
                              </span>
                            ) : (
                              "\u2014"
                            )}
                          </TableCell>
                          <TableCell>
                            {run.loss_curve_analysis ? (
                              <span
                                className={
                                  run.loss_curve_analysis.underfitting_score > 0.5
                                    ? "text-yellow-500"
                                    : "text-green-500"
                                }
                              >
                                {run.loss_curve_analysis.underfitting_score.toFixed(2)}
                              </span>
                            ) : (
                              "\u2014"
                            )}
                          </TableCell>
                          <TableCell>
                            <div className="flex flex-wrap gap-1.5">
                              {keyParams.map((k) => (
                                <span
                                  key={k}
                                  className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-muted text-xs font-mono"
                                >
                                  <span className="text-muted-foreground">{k.replace(/_/g, " ")}:</span>
                                  <span className="font-medium">{formatValue(run.hyperparameters[k])}</span>
                                </span>
                              ))}
                            </div>
                          </TableCell>
                        </TableRow>
                        {isExpanded && (
                          <TableRow className="bg-muted/30 hover:bg-muted/30">
                            <TableCell colSpan={7} className="p-0">
                              <div className="px-6 py-5">
                                <h4 className="text-sm font-gc-med text-foreground mb-4">
                                  All Hyperparameters
                                </h4>
                                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
                                  {Object.entries(run.hyperparameters).map(([k, v]) => (
                                    <div key={k} className="space-y-1">
                                      <p className="text-sm text-muted-foreground font-gc-reg">
                                        {k.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())}
                                      </p>
                                      <p className="text-sm font-gc-reg text-foreground">{formatValue(v)}</p>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            </TableCell>
                          </TableRow>
                        )}
                      </React.Fragment>
                    );
                  })}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );

  const tabDefinitions = [
    { key: "overview", label: "Overview", content: overviewContent },
    { key: "wave-runs", label: "Wave Runs", content: waveRunsContent },
  ];

  const tabs: TabItem[] = tabDefinitions.map((t) => ({ key: t.key, label: t.label }));

  return (
    <div className="max-w-6xl mx-auto px-8 py-8 space-y-8">
      {/* Header Card */}
      <Card className="border-border">
        <CardContent className="p-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Button variant="ghost" size="sm" onClick={() => navigate("/admin/compass")}>
                <ArrowLeftIcon className="w-4 h-4 mr-1" />
                Back
              </Button>
              <h1 className="text-2xl font-semibold tracking-tight">
                Search #{search.id}
              </h1>
              <Badge className={STATUS_VARIANT[search.status] || ""}>
                {search.status}
              </Badge>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={() => { loadSearch(); loadRuns(); }}>
                <RefreshCwIcon className="w-4 h-4 mr-1" />
                Refresh
              </Button>
              {isActive && (
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={() => setCancelDialogOpen(true)}
                >
                  <XCircleIcon className="w-4 h-4 mr-1" />
                  Cancel
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tabs */}
      <Card className="border-border">
        <CardContent className="p-0">
          <OverflowTabs
            tabs={tabs}
            activeTab={activeTab}
            onTabChange={setActiveTab}
            className="border-b border-border px-8 py-4"
          />
          <div className="p-8">
            {tabDefinitions.find((t) => t.key === activeTab)?.content}
          </div>
        </CardContent>
      </Card>

      <AlertDialog open={cancelDialogOpen} onOpenChange={setCancelDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Cancel Compass Search</AlertDialogTitle>
            <AlertDialogDescription>
              This will cancel all in-progress finetune runs and stop the search.
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep Running</AlertDialogCancel>
            <AlertDialogAction onClick={handleCancel} disabled={cancelling}>
              {cancelling ? "Cancelling..." : "Cancel Search"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default CompassSearchDetail;
